import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/sidebar.css";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useGlobal } from "../GlobalContext";

// Icons
import flyjaclogo from "../assets/svg/flyjac-logo.png";
import dashboardIcon from "../assets/svg/dashboard_icon.svg";
import CostModelIcon from "../assets/svg/CosteModelCreation.svg";
import ElementCreationIcon from "../assets/svg/ElementCreation.svg";
import ProjectCreationIcon from "../assets/svg/ProjectCreation.svg";
import contractIcon from "../assets/svg/contract_icon.svg";
import jobsIcon from "../assets/svg/jobs_icon.svg";
import invoiceIcon from "../assets/svg/invoice_icon.svg";
import mastersIcon from "../assets/svg/masters_icon.svg";
import ApprovalIcon from "../assets/svg/Approval.svg";
import ReportIcon from "../assets/svg/Report.svg";
import mailConfigIcon from "../assets/svg/mailConfigIcon.svg";
import billingAutomationIcon from "../assets/svg/BillingAutomationIcon.svg";

// Static menu configuration, gated by role permission (menu_permission).
// `name` must match the permission name returned by get_role_permission.
const MENU_ITEMS = [
  /* { name: "Dashboard", path: "/dashboard", icon: dashboardIcon }, */
  { name: "Cost Model Creation", path: "/scope", icon: CostModelIcon },
  { name: "Element Creation", path: "/CostType", icon: ElementCreationIcon },
  { name: "Project Creation", path: "/projects", icon: ProjectCreationIcon },
 /*  { name: "Contract", path: "/contract", icon: contractIcon },
  { name: "Jobs", path: "/jobs", icon: jobsIcon },
  { name: "Invoice", path: "/salesInvoice", icon: invoiceIcon },
  { name: "Masters", path: "/chargecode", icon: mastersIcon },
  { name: "Approval Configuration", path: "/approval_config", icon: ApprovalIcon },
  { name: "Report", path: "/report", icon: ReportIcon },
  { name: "Mail Configuration", path: "/mail_queue", icon: mailConfigIcon },
  { name: "Billing Automation", path: "/billingAutomation", icon: billingAutomationIcon }, */
];

// Route prefixes that mark a menu item as active, beyond its primary path.
const ACTIVE_PATH_MATCHERS = {
  "Cost Model Creation": ["/scope", "/operation", "/category", "/subcategory", "/modelname", "/Salarydata"],
  "Element Creation": ["/CostType", "/CostElement", "/SubCostElement", "/ElementStandard", "/Template", "/UOM", "/ApprovalLinkage", "/Assumptions", "/Terms"],
  "Project Creation": ["/projects", "/projectsPeriod", "/opperationalAssumption", "/financialAssumption", "/costLine"],
  Contract: ["/contract", "/contracts_popup", "/contractviewpage"],
  Jobs: ["/jobs", "/SalesViewPage", "/CostViewPage"],
  Invoice: ["/invoice", "/salesInvoice", "/costInvoice", "/salesBillPage", "/costBillPage"],
  Masters: ["/chargecode", "/jobtype", "/plantCode", "/user", "/roleMaster", "/designation", "/vendor"],
  "Approval Configuration": ["/approval_config", "/approval_creation"],
  "Mail Configuration": ["/mail_queue", "/mailTemplate"],
  "Billing Automation": ["/billingAutomation"],
};

const Sidebar = ({ expanded, setExpanded }) => {
  const { rolePermissionList, orgImage } = useGlobal();
  const navigate = useNavigate();
  const location = useLocation();

  const isCollapsed = !expanded;

  useEffect(() => {
    if (expanded) {
      document.body.classList.add("sidebar-expanded");
    } else {
      document.body.classList.remove("sidebar-expanded");
    }
  }, [expanded]);

  // True when the role has menu_permission for the given menu name.
  const checkPermission = (menuName, permission) =>
    rolePermissionList.some((element) => element.name === menuName && element[permission] === true);

  // Determine which menu item the current route belongs to.
  const isActive = (item) => {
    const matchers = ACTIVE_PATH_MATCHERS[item.name] || [item.path];
    return matchers.some((prefix) => location.pathname.startsWith(prefix));
  };

  const visibleItems = MENU_ITEMS.filter((item) => checkPermission(item.name, "menu_permission"));

  return (
    <div className={`sidebar ${expanded ? "expanded" : ""}`}>
      <div className="sidebar-logo fixed-logo">
        <img
          src={orgImage != null && orgImage !== "" ? orgImage : flyjaclogo}
          alt="Sidebar Logo"
          className="sidebar-expanded-icon"
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/dashboard")}
        />
      </div>

      <button
        type="button"
        className="sidebar-collapse-toggle"
        onClick={() => setExpanded(!expanded)}
        style={{
          position: "absolute",
          right: "-16px",
          top: "40px",
          width: "32px",
          height: "32px",
          borderRadius: "50%",
          background: "#FFFFFF",
          border: "1px solid #E5E5E5",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
          zIndex: 10001,
          outline: "none",
        }}
      >
        {isCollapsed ? <ChevronRight size={18} color="#6929E2" /> : <ChevronLeft size={18} color="#6929E2" />}
      </button>

      <div className="sidebar-content">
        <ul>
          {visibleItems.map((item) => (
            <li key={item.name}>
              <Link
                to={item.path}
                title={!expanded ? item.name : undefined}
                className={`menu-link ${isActive(item) ? "active" : ""}`}
              >
                <div className="sidebar-icon-wrapper">
                  <img
                    src={item.icon}
                    alt={item.name}
                    className={`menu-icon ${item.name === "Dashboard" ? "dashboard-icon" : ""}`}
                  />
                </div>
                {expanded && <span className="menu-text">{item.name}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
